import { useEffect, useMemo, useState } from 'react';
import { Search, SlidersHorizontal, X, User, Heart, ImageIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { MoreFilters, MORE_FILTER_KEYS } from './MoreFilters';
import { categoryStyle, FILTER_LABELS } from '../../lib/summaries';

const SORTS = [
  { id: 'newest', label: 'الأحدث' }, { id: 'likes', label: 'الأكثر إعجاباً' },
  { id: 'pages', label: 'الأكثر صفحات' }, { id: 'alpha', label: 'أبجدياً' },
];
const QUICK = [
  { key: 'mine', label: 'ملخصاتي', icon: User }, { key: 'liked', label: 'ما أعجبني', icon: Heart },
  { key: 'with_image', label: 'مع صورة فقط', icon: ImageIcon },
];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 h-8 rounded-full border text-xs font-semibold transition-colors duration-150 ${active ? 'bg-[#C19A6B] border-[#C19A6B] text-white' : 'bg-white border-[#C19A6B]/20 text-[#5D4037] hover:bg-[#C19A6B]/10'}`}>
    {children}
  </button>
);

export const SummariesToolbar = ({ filters, setFilters, categories, total, compact }) => {
  const [search, setSearch] = useState(filters.q || '');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  useEffect(() => { setSearch(filters.q || ''); }, [filters.q]);
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.q || '') !== search) setFilters({ q: search }); }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const moreKey = MORE_FILTER_KEYS.map((k) => filters[k] || '').join('|');
  const moreValues = useMemo(() => Object.fromEntries(MORE_FILTER_KEYS.map((k) => [k, filters[k] || ''])), [moreKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const applyMore = (v) => { setFilters(v); setSheetOpen(false); setPopOpen(false); };
  const active = Object.entries(filters).filter(([k, v]) => v && k !== 'sort');

  return (
    <>
    <div className={`sticky top-0 z-30 -mx-4 md:-mx-8 px-4 md:px-8 bg-[#F9F5F0]/95 backdrop-blur-md border-b border-[#C19A6B]/20 transition-[padding,box-shadow] duration-200 ${compact ? 'py-2 shadow-sm' : 'py-4'}`} data-testid="summaries-toolbar">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative lg:w-80">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ابحث في العنوان، المؤلف، القارئ، النص..."
            className="w-full h-10 rounded-xl border border-[#C19A6B]/25 bg-white pr-9 pl-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C19A6B]/60 transition-shadow" aria-label="بحث" data-testid="summaries-search-input" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filters.sort || 'newest'} onChange={(e) => setFilters({ sort: e.target.value })}
            className="h-9 rounded-xl border border-[#C19A6B]/25 bg-white px-3 text-xs font-semibold text-[#5D4037] focus:outline-none focus:ring-2 focus:ring-[#C19A6B]/60" aria-label="الترتيب" data-testid="summaries-sort-select">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {QUICK.map(({ key, label, icon: Icon }) => (
            <Chip key={key} active={!!filters[key]} onClick={() => setFilters({ [key]: filters[key] ? '' : '1' })} testId={`quick-${key}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </Chip>
          ))}
          <div className="hidden md:block">
            <Popover open={popOpen} onOpenChange={setPopOpen}>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#C19A6B]/20 bg-white text-xs font-semibold text-[#5D4037] hover:bg-[#C19A6B]/10 transition-colors" data-testid="more-filters-btn">
                  <SlidersHorizontal className="w-3.5 h-3.5" /> المزيد
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 bg-white border-[#C19A6B]/20 shadow-xl"><MoreFilters values={moreValues} onApply={applyMore} /></PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <div className={`flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] transition-[margin] duration-200 ${compact ? 'mt-2' : 'mt-3'}`} data-testid="summaries-categories">
        <Chip active={!filters.category} onClick={() => setFilters({ category: '' })} testId="category-chip-all">الكل <span className="opacity-70">{total}</span></Chip>
        {categories.map((c) => {
          const Icon = categoryStyle(c.name).icon;
          return (
            <Chip key={c.name} active={filters.category === c.name} onClick={() => setFilters({ category: filters.category === c.name ? '' : c.name })} testId={`category-chip-${c.name}`}>
              <Icon className="w-3.5 h-3.5" /> {c.name} <span className="opacity-70">{c.count}</span>
            </Chip>
          );
        })}
      </div>

      {active.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="active-filters">
          {active.map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#5D4037]/8 text-[#5D4037] text-[11px] font-semibold">
              {(FILTER_LABELS[k] || ((x) => x))(v)}
              <button onClick={() => setFilters({ [k]: '' })} aria-label="إزالة" className="hover:text-red-600" data-testid={`remove-filter-${k}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={() => setFilters(Object.fromEntries(active.map(([k]) => [k, ''])))} className="text-[11px] font-bold text-red-600 hover:underline" data-testid="clear-all-filters">مسح الكل</button>
        </div>
      )}
    </div>

      <button onClick={() => setSheetOpen(true)} className="md:hidden fixed bottom-24 left-4 z-40 w-12 h-12 rounded-full bg-[#C19A6B] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform" aria-label="المزيد من الفلاتر" data-testid="more-filters-fab">
        <SlidersHorizontal className="w-5 h-5" />
      </button>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-[#FCFAF6] max-h-[90vh] overflow-y-auto" dir="rtl">
          <SheetTitle className="mb-4 text-right">تصفية إضافية</SheetTitle>
          <MoreFilters values={moreValues} onApply={applyMore} />
        </SheetContent>
      </Sheet>
    </>
  );
};
